import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Palette, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSignedStorageUrl } from '@/hooks/useSignedStorageUrl';
import { useAvatarList } from '@/hooks/useAvatarList';


const BACKGROUND_COLORS = [
  { name: 'Grijs', value: '#E5E7EB' },
  { name: 'Blauw', value: '#3B82F6' },
  { name: 'Groen', value: '#10B981' },
  { name: 'Paars', value: '#8B5CF6' },
  { name: 'Roze', value: '#EC4899' },
  { name: 'Oranje', value: '#F59E0B' },
  { name: 'Rood', value: '#EF4444' },
  { name: 'Indigo', value: '#6366F1' }
];

interface AvatarSelectorProps {
  selectedAvatar: string;
  selectedBgColor: string;
  onAvatarChange: (avatar: string) => void;
  onBgColorChange: (color: string) => void;
  fallbackText: string;
}

const AvatarPreview: React.FC<{ avatarPath: string; bgColor: string; fallbackText: string }> = ({ 
  avatarPath, 
  bgColor, 
  fallbackText 
}) => {
  const { url } = useSignedStorageUrl('pictures', avatarPath);
  
  return (
    <Avatar className="h-12 w-12 mx-auto" style={{ backgroundColor: bgColor }}>
      <AvatarImage src={url || undefined} />
      <AvatarFallback style={{ backgroundColor: bgColor }} className="text-white text-xs">
        {fallbackText}
      </AvatarFallback>
    </Avatar>
  );
};

export const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  selectedAvatar,
  selectedBgColor,
  onAvatarChange,
  onBgColorChange,
  fallbackText
}) => {
  const [customColor, setCustomColor] = useState(selectedBgColor);
  const { avatars, loading: avatarsLoading } = useAvatarList();
  
  // Normalize selected avatar path to ensure it's in avatars/ folder format
  const normalizedSelectedAvatar = selectedAvatar?.startsWith('avatars/') 
    ? selectedAvatar 
    : selectedAvatar ? `avatars/${selectedAvatar}` : '';
    
  const { url: selectedAvatarUrl } = useSignedStorageUrl('pictures', normalizedSelectedAvatar);

  const handleCustomColorChange = (value: string) => {
    setCustomColor(value);
    onBgColorChange(value);
  };

  const handleAvatarSelect = (avatarPath: string) => {
    // Store the full path including avatars/ folder
    onAvatarChange(avatarPath);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Preview Avatar */}
      <div className="flex justify-center">
        <Avatar className="h-20 w-20 sm:h-24 sm:w-24" style={{ backgroundColor: selectedBgColor }}>
          <AvatarImage src={selectedAvatarUrl || undefined} />
          <AvatarFallback style={{ backgroundColor: selectedBgColor }} className="text-white font-semibold text-base sm:text-lg">
            {fallbackText}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Avatar Selection */}
      <div>
        <h3 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3">Kies een avatar</h3>
        {avatarsLoading ? (
          <div className="flex items-center justify-center p-6 sm:p-8">
            <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-xs sm:text-sm text-muted-foreground">Avatars laden...</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {avatars.map((avatarPath, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleAvatarSelect(avatarPath)}
                className={cn(
                  "relative rounded-lg border-2 p-1.5 sm:p-2 transition-colors hover:border-primary",
                  normalizedSelectedAvatar === avatarPath ? "border-primary" : "border-border"
                )}
              >
                <AvatarPreview 
                  avatarPath={avatarPath} 
                  bgColor={selectedBgColor} 
                  fallbackText={fallbackText} 
                />
                {normalizedSelectedAvatar === avatarPath && (
                  <div className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 bg-primary rounded-full flex items-center justify-center">
                    <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary-foreground" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Background Color Selection */}
      <div>
        <h3 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3">Kies een achtergrondkleur</h3>
        <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {BACKGROUND_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => onBgColorChange(color.value)}
              className={cn(
                "relative h-8 sm:h-10 w-full rounded-lg border-2 transition-colors",
                selectedBgColor === color.value ? "border-primary" : "border-border"
              )}
              style={{ backgroundColor: color.value }}
              title={color.name}
            >
              {selectedBgColor === color.value && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Check className="h-3 w-3 sm:h-4 sm:w-4 text-white drop-shadow-sm" />
                </div>
              )}
            </button>
          ))}
        </div>
        
        {/* Custom Color Picker */}
        <div className="space-y-2">
          <Label htmlFor="custom-color" className="text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2">
            <Palette className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Aangepaste kleur
          </Label>
          <div className="flex gap-2">
            <Input
              id="custom-color"
              type="color"
              value={customColor}
              onChange={(e) => handleCustomColorChange(e.target.value)}
              className="w-12 sm:w-16 h-9 sm:h-10 p-1 border rounded cursor-pointer"
            />
            <Input
              type="text"
              placeholder="#FF0000"
              value={customColor}
              onChange={(e) => handleCustomColorChange(e.target.value)}
              className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>
      </div>
    </div>
  );
};